/**
 * 转化漏斗类型定义
 * 实现"2段式（分析即预览 -> 解锁即执行）"转化漏斗的前端类型
 * Requirements: 8.1, 8.2
 */

// ============================================
// 分析三维卡片 (Insight Triad)
// Requirements: 1.1-1.6, 2.1-2.5, 3.1-3.4
// ============================================

/**
 * 视觉证据
 * 展示 AI 检测到的视觉特征
 */
export interface VisualEvidence {
  /** 身体部位 (e.g., "体表", "鳍部") */
  body_part: string;
  /** 视觉特征 (e.g., "白点", "充血") - 使用 sign 而非 symptom */
  sign: string;
  /** 包围盒 X 坐标 (百分比, 0-100) */
  bbox_x?: number;
  /** 包围盒 Y 坐标 (百分比, 0-100) */
  bbox_y?: number;
}

/**
 * 严重程度等级
 */
export type SeverityLevel = "low" | "medium" | "high";

/**
 * 严重程度数据
 * 用于风险评估卡片
 */
export interface SeverityData {
  /** 严重程度等级 */
  level: SeverityLevel;
  /** 颜色代码 (e.g., "#FF4D4F" for high) */
  color: string;
  /** 紧迫性文案 (e.g., "急性传染期，建议立即干预") */
  urgency_text: string;
}

/**
 * 分析三维卡片数据
 * 包含视觉证据、风险评估、成因推断
 */
export interface AnalysisCardData {
  /** 状况显示名称 (e.g., "白点病 (Ichthyophthirius)") */
  condition_display: string;
  /** 置信度 (0-100) */
  confidence: number;
  /** 视觉证据列表 */
  visual_evidence: VisualEvidence[];
  /** 严重程度数据 */
  severity: SeverityData;
  /** 成因推断文案 (e.g., "疑似温差过大导致免疫力下降") */
  root_cause_inference: string;
}

// ============================================
// 资源匹配锚点 (Inventory Anchor)
// Requirements: 4.1-4.5
// ============================================

/**
 * 工具箱匹配数据
 * 连接用户工具箱与养护方案
 */
export interface InventoryMatchData {
  /** 准备度分数 (0-100) */
  score: number;
  /** 节省费用文案 (e.g., "预计可为您节省 ¥35") */
  saved_money_text: string | null;
  /** 已有物品列表 */
  has_items: string[];
  /** 缺失物品列表 */
  missing_items: string[];
}

// ============================================
// 锁定参数时间轴 (Locked Timeline)
// Requirements: 5.1-5.5, 6.1-6.5
// ============================================

/**
 * Day 0 任务
 * 紧急养护期的具体任务
 */
export interface Day0Task {
  /** 动作名称 (e.g., "调节水温", "投放粗盐") */
  action: string;
  /** 参数值 (锁定时为 null, 解锁后为实际值) */
  param_value: number | null;
  /** 参数单位 (e.g., "°C", "g", "%") */
  param_unit: string;
  /** 锁定原因 (e.g., "基于您的鱼缸体积精准计算") */
  lock_reason: string;
  /** 使用的工具箱物品 (e.g., "加热棒", "粗盐") */
  toolbox_item_used?: string;
}

/**
 * 关键节点
 * 养护周期中的重要时间点
 */
export interface Milestone {
  /** 天数 (0, 1, 4, 7...) */
  day: number;
  /** 标题 (e.g., "紧急处理", "观察期", "换水排毒", "恢复期") */
  title: string;
  /** 摘要 (用于无具体任务的阶段, e.g., "监测白点消退情况") */
  summary?: string;
  /** 具体任务 (用于有任务的阶段) */
  tasks?: Day0Task[];
}

/**
 * 时间轴预览数据
 * 展示养护流程但隐藏关键参数
 */
export interface TimelinePreviewData {
  /** 是否已解锁 */
  is_unlocked: boolean;
  /** 工具箱匹配数据 */
  inventory_match: InventoryMatchData;
  /** Day 0 任务列表 */
  day_0_tasks: Day0Task[];
  /** 总养护天数 */
  total_days: number;
  /** 养护阶段 (e.g., ["紧急处理", "观察", "换水", "恢复"]) */
  phases: string[];
  /** 关键节点预览 */
  milestones: Milestone[];
}

// ============================================
// API 响应类型
// Requirements: 8.1-8.5
// ============================================

/**
 * 评估转化漏斗响应属性
 */
export interface AssessmentConversionAttributes {
  /** 评估 ID */
  id: number;
  /** 友好 ID */
  friendly_id: string;
  /** 状态 */
  status: "pending" | "processing" | "completed" | "failed" | "rejected";
  /** 图片 URL */
  image_url: string | null;
  /** 缩略图 URL */
  thumb_url: string | null;
  /** 创建时间 (ISO8601) */
  created_at: string;
  /** 完成时间 (ISO8601) */
  completed_at: string | null;
  /** 分析三维卡片数据 */
  analysis_card: AnalysisCardData | null;
  /** 时间轴预览数据 */
  timeline_preview: TimelinePreviewData;
}

/**
 * 评估转化漏斗 API 响应
 * GET /api/v1/assessments/:id?conversion=true
 */
export interface AssessmentConversionResponse {
  data: {
    type: "assessment";
    attributes: AssessmentConversionAttributes;
  };
}

// ============================================
// 组件 Props 类型
// ============================================

/**
 * InsightTriad 组件 Props
 */
export interface InsightTriadProps {
  /** 分析卡片数据 */
  analysisCard: AnalysisCardData;
  /** 图片 URL */
  imageUrl?: string;
  /** 自定义类名 */
  className?: string;
}

/**
 * VisualEvidenceCard 组件 Props
 */
export interface VisualEvidenceCardProps {
  /** 图片 URL */
  imageUrl: string;
  /** 状况显示名称 */
  conditionDisplay: string;
  /** 置信度 */
  confidence: number;
  /** 视觉证据列表 */
  visualEvidence: VisualEvidence[];
  /** 自定义类名 */
  className?: string;
}

/**
 * RiskAssessmentCard 组件 Props
 */
export interface RiskAssessmentCardProps {
  /** 严重程度数据 */
  severity: SeverityData;
  /** 自定义类名 */
  className?: string;
}

/**
 * RootCauseCard 组件 Props
 */
export interface RootCauseCardProps {
  /** 成因推断文案 */
  inference: string;
  /** 鱼缸信息 */
  tankInfo?: {
    name: string;
    hasHeater: boolean;
    volume: number;
  };
  /** 自定义类名 */
  className?: string;
}

/**
 * InventoryAnchor 组件 Props
 */
export interface InventoryAnchorProps {
  /** 工具箱匹配数据 */
  inventoryMatch: InventoryMatchData;
  /** 自定义类名 */
  className?: string;
}

/**
 * LockedTimeline 组件 Props
 */
export interface LockedTimelineProps {
  /** 时间轴预览数据 */
  timelinePreview: TimelinePreviewData;
  /** 是否已解锁 */
  isUnlocked: boolean;
  /** 解锁回调 */
  onUnlock: () => void;
  /** 自定义类名 */
  className?: string;
}

/**
 * LockedParamTooltip 组件 Props
 */
export interface LockedParamTooltipProps {
  /** 锁定原因 */
  lockReason: string;
  /** 鱼缸体积 */
  tankVolume?: number;
  /** 子元素 */
  children: React.ReactNode;
}

// ============================================
// 工具函数类型
// ============================================

/**
 * 置信度颜色映射
 * @param confidence 置信度 (0-100)
 * @returns 颜色类名
 */
export type ConfidenceColorFn = (
  confidence: number
) => "green" | "yellow" | "red";

/**
 * 严重程度颜色映射
 * @param level 严重程度等级
 * @returns 颜色代码
 */
export type SeverityColorFn = (level: SeverityLevel) => string;

// ============================================
// 常量
// ============================================

/** 严重程度颜色映射 */
export const SEVERITY_COLORS: Record<SeverityLevel, string> = {
  high: "#FF4D4F",
  medium: "#FAAD14",
  low: "#52C41A",
};

/** 置信度阈值 */
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 50,
} as const;

/**
 * 获取置信度颜色
 * @param confidence 置信度 (0-100)
 * @returns 颜色类名
 */
export function getConfidenceColor(
  confidence: number
): "green" | "yellow" | "red" {
  if (confidence > CONFIDENCE_THRESHOLDS.HIGH) return "green";
  if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return "yellow";
  return "red";
}

/**
 * 获取严重程度颜色
 * @param level 严重程度等级
 * @returns 颜色代码
 */
export function getSeverityColor(level: SeverityLevel): string {
  return SEVERITY_COLORS[level] || SEVERITY_COLORS.low;
}
